import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ETAPaymentMethod, isPhonePaymentMethod } from '~/api/ETA';
import { PaymentSystem } from '~/api/Order';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import Spinner from '~/components/Spinner/Spinner';
import { useGlobal } from '~/hooks/useGlobal';
import { CardDisclaimer } from '~/pages/Checkout/CardDisclaimer';
import CARD_BRANDS from '~/pages/Checkout/cardBrands';
import {
  CurrentPaymentMethod,
  handlePaymentsChange,
  usePaymentMethodInit,
} from '~/pages/Checkout/components/PaymentMethods/paymentMethodsShared';
import MobileMoneyPayment from '~/pages/Checkout/MobileMoneyPayment';
import { checkoutStore, orderStore } from '~/stores';
import AdditionalPaymentMethod from '~/stores/CheckoutStore/AdditionalPaymentMethod';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import type PaymentMethod from '~/stores/CheckoutStore/PaymentMethod';

import { CashPayment2 } from '../CashPayment2/CashPayment2';

import styles from './PaymentMethods2.module.scss';

export const PaymentMethods2 = ({
  hideDisclaimer,
}: {
  hideDisclaimer?: boolean;
}) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useGlobal();

  usePaymentMethodInit();

  const methodItemContent: Record<PaymentMethod, JSX.Element> = {
    [MobilePaymentMethod.ApplePay]: (
      <>
        <Icon
          {...(isMobile
            ? { type: 'apay2', size: 38 }
            : { type: 'apay', size: 24 })}
        />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>{t('applePay')}</span>
        </div>
        <input
          data-checkbox="apple-pay"
          type="checkbox"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === MobilePaymentMethod.ApplePay
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(MobilePaymentMethod.ApplePay)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [MobilePaymentMethod.GooglePay]: (
      <>
        <Icon
          type="gpay"
          {...(isMobile
            ? { width: 46, height: 24 }
            : { width: 60, height: 32 })}
        />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>{t('googlePay')}</span>
        </div>
        <input
          data-checkbox="google-pay"
          type="checkbox"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === MobilePaymentMethod.GooglePay
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(MobilePaymentMethod.GooglePay)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [ETAPaymentMethod.Card]: (
      <>
        <Icon type="card" size={24} />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>
            {t(
              company.match({
                vilo: 'payOnline',
                default: 'newCardOnline',
              }),
            )}
          </span>
        </div>
        <input
          data-checkbox="card"
          type="checkbox"
          name="payment-type"
          checked={checkoutStore.activePaymentMethod === ETAPaymentMethod.Card}
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.Card)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [ETAPaymentMethod.Cash]: (
      <>
        <Icon type="cash" size={24} />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>{t('cash')}</span>
        </div>
        <input
          data-checkbox="cash"
          type="checkbox"
          name="payment-type"
          checked={checkoutStore.activePaymentMethod === ETAPaymentMethod.Cash}
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.Cash)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [ETAPaymentMethod.PhoneAirtel]: (
      <>
        <Icon type="airtel" size={24} />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>{t('paymentVia', { operator: 'Airtel' })}</span>
        </div>
        <input
          data-checkbox="mobile-money-airtel"
          type="checkbox"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === ETAPaymentMethod.PhoneAirtel
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.PhoneAirtel)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [ETAPaymentMethod.PhoneMoov]: (
      <>
        <Icon type="moov" size={24} />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>{t('paymentVia', { operator: 'Moov' })}</span>
        </div>
        <input
          data-checkbox="mobile-money-moov"
          type="checkbox"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === ETAPaymentMethod.PhoneMoov
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.PhoneMoov)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [ETAPaymentMethod.QR]: (
      <>
        <Icon type="qr-code" size={24} />
        <div
          className={styles['panel-section__content-payment-type-desctiption']}
        >
          <span>{t('mobileMoneyQR')}</span>
        </div>
        <input
          data-checkbox="mobile-money-qr"
          type="checkbox"
          name="payment-type"
          checked={checkoutStore.activePaymentMethod === ETAPaymentMethod.QR}
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.QR)
          }
        />
        <span className={styles['panel-section__content-payment-type-check']} />
      </>
    ),
    [AdditionalPaymentMethod.KNET]: (
      <div className="d-flex align-items-center">
        <img className="mr-6 flex-shrink-0" src={''} height="14" alt="" />
        {t('knet')}
      </div>
    ),
    [AdditionalPaymentMethod.VisaMastercard]: (
      <>
        {orderStore.paymentCards?.map?.(
          ({ card: { brand: brandName, last4 }, id }) => {
            const cardBrand = brandName ? CARD_BRANDS[brandName] : '';
            const brandUi = cardBrand && (
              <>
                <img
                  src={cardBrand.icon}
                  alt={`payment system: ${brandName}`}
                />
                <div
                  className={
                    styles['panel-section__content-payment-type-desctiption']
                  }
                >
                  <span>{cardBrand.title}</span>
                  <span
                    className={
                      styles[
                        'panel-section__content-payment-type-desctiption-card'
                      ]
                    }
                  >
                    •• {last4}
                  </span>
                </div>
              </>
            );
            return (
              <Fragment key={id}>
                {brandUi}
                <input
                  data-checkbox={brandName}
                  type="checkbox"
                  name="payment-type"
                  checked={
                    checkoutStore.activePaymentMethod ===
                      AdditionalPaymentMethod.VisaMastercard &&
                    id === checkoutStore.paymentsMethodId
                  }
                  onChange={() => {
                    checkoutStore.setActivePaymentMethod(
                      AdditionalPaymentMethod.VisaMastercard,
                    );
                    handlePaymentsChange(id);
                  }}
                />
                <span
                  className={
                    styles['panel-section__content-payment-type-check']
                  }
                />
              </Fragment>
            );
          },
        )}
      </>
    ),
  };

  return (
    <>
      <div className={styles.paymentPanel}>
        <div className={styles['panel-section']}>
          <div className={styles['panel-section__content-payment']}>
            <p className={styles['panel-section__title']}>
              {t('selectPaymentMethod')}
            </p>
            <div className={styles['panel-section__content-payment-grid']}>
              {checkoutStore.availablePaymentMethods
                .filter((method) => method !== ETAPaymentMethod.QR || !isTablet)
                .map((method) => {
                  return (
                    <label
                      className={styles['panel-section__content-payment-type']}
                      key={method}
                    >
                      {methodItemContent[method]}
                    </label>
                  );
                })}
            </div>

            {isMobile &&
              checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
              !isPhonePaymentMethod(
                checkoutStore.activePaymentMethod as ETAPaymentMethod,
              ) &&
              checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR && (
                <div
                  className={styles['panel-section__content-payment-guarantee']}
                >
                  <Icon
                    type="shield"
                    size={16}
                    className={
                      styles['panel-section__content-payment-guarantee-icon']
                    }
                  />
                  <p
                    className={
                      styles[
                        'panel-section__content-payment-guarantee-description'
                      ]
                    }
                  >
                    {t('securePayment')}
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>
      {checkoutStore.activePaymentSystem !== PaymentSystem.octo && (
        <div className={styles.paymentPanel}>
          <div className={styles['panel-section']}>
            {checkoutStore.activePaymentMethod === ETAPaymentMethod.Cash && (
              <CashPayment2 />
            )}
            {(isPhonePaymentMethod(
              checkoutStore.activePaymentMethod as ETAPaymentMethod,
            ) ||
              checkoutStore.activePaymentMethod === ETAPaymentMethod.QR) && (
              <MobileMoneyPayment />
            )}

            <div className={styles['panel-section__content-payment-add']}>
              <CurrentPaymentMethod />
              {!hideDisclaimer && (
                <>
                  {checkoutStore.activePaymentMethod !==
                    ETAPaymentMethod.Cash &&
                    !company.hideFreezeMoneyMessage && (
                      <p
                        className={
                          styles['panel-section__content-payment-add-text']
                        }
                      >
                        {t('phrases:moneyFreeze')}
                      </p>
                    )}
                </>
              )}
              {company.showCardDisclaimer &&
                checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash && (
                  <CardDisclaimer />
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default observer(PaymentMethods2);
